import { useCallback, useEffect, useState } from 'react'

const ENDPOINT = process.env.REACT_APP_LARAVEL_API_ENDPOINT

function orgChartOf(profile) {
  if (!profile || profile.user_type !== 2 || !profile.org_chart) return null
  try {
    return JSON.parse(profile.org_chart)
  } catch (e) {
    return null
  }
}

function postJson(path, body, token) {
  return fetch(`${ENDPOINT}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
  }).then(res => res.json())
}

export default function useOrgFilters(auth) {
  const { profile, accessToken } = auth

  const [options, setOptions] = useState({
    division: [],
    department: [],
    section: [],
    plant: [],
  })
  const [loading, setLoading] = useState({
    division: false,
    department: false,
    section: false,
    plant: false,
  })
  const [selected, setSelected] = useState({
    division: [],
    department: [],
    section: [],
    plant: [],
    plantKeys: [],
  })

  const setBusy = (key, value) =>
    setLoading(prev => ({ ...prev, [key]: value }))

  const loadDivision = useCallback(() => {
    const chart = orgChartOf(profile)
    setBusy('division', true)
    return postJson(
      '/getdivision',
      {
        division_visibility: chart
          ? chart.divisions.map(d => d.divisionNo)
          : [],
        comp: [2, 3, 4, 5, 6],
        company_id: profile.company_id,
      },
      accessToken,
    )
      .then(data => setOptions(prev => ({ ...prev, division: data })))
      .finally(() => setBusy('division', false))
  }, [profile, accessToken])

  const loadDepartment = useCallback(
    divisions => {
      const chart = orgChartOf(profile)
      setBusy('department', true)
      return postJson(
        '/getdepartment',
        {
          department_visibility: chart ? chart.departments : [],
          division: divisions,
          company_id: profile.company_id,
        },
        accessToken,
      )
        .then(data => setOptions(prev => ({ ...prev, department: data })))
        .finally(() => setBusy('department', false))
    },
    [profile, accessToken],
  )

  const loadSection = useCallback(
    departments => {
      const chart = orgChartOf(profile)
      setBusy('section', true)
      return postJson(
        '/getsection',
        {
          section_visibility: chart ? chart.sections : [],
          department: departments,
          company_id: profile.company_id,
        },
        accessToken,
      )
        .then(data => setOptions(prev => ({ ...prev, section: data })))
        .finally(() => setBusy('section', false))
    },
    [profile, accessToken],
  )

  const loadPlant = useCallback(
    sections => {
      const chart = orgChartOf(profile)
      setBusy('plant', true)
      return postJson(
        '/getplantforconcretedashboard',
        {
          location_visibility: profile.location_visibility,
          section_visibility: chart ? chart.sections : [],
          section: sections,
          relate_company: `${profile.relate_company}`,
        },
        accessToken,
      )
        .then(data => setOptions(prev => ({ ...prev, plant: data })))
        .finally(() => setBusy('plant', false))
    },
    [profile, accessToken],
  )

  useEffect(() => {
    loadDivision()
  }, [loadDivision])

  const onDivisionChange = useCallback(
    value => {
      setSelected({
        division: value,
        department: [],
        section: [],
        plant: [],
        plantKeys: [],
      })
      loadDepartment(value).then(() =>
        loadSection([]).then(() => loadPlant([])),
      )
    },
    [loadDepartment, loadSection, loadPlant],
  )

  const onDepartmentChange = useCallback(
    value => {
      setSelected(prev => ({
        ...prev,
        department: value,
        section: [],
        plant: [],
        plantKeys: [],
      }))
      loadSection(value).then(() => loadPlant([]))
    },
    [loadSection, loadPlant],
  )

  const onSectionChange = useCallback(
    value => {
      setSelected(prev => ({
        ...prev,
        section: value,
        plant: [],
        plantKeys: [],
      }))
      loadPlant(value)
    },
    [loadPlant],
  )

  const onPlantChange = useCallback((value, nodes) => {
    setSelected(prev => ({
      ...prev,
      plant: nodes.map(n => Number(n.key)),
      plantKeys: value,
    }))
  }, [])

  // ยกเลิกในหน้าต่างตัวกรอง: คืนค่าที่เลือกไว้ พร้อมโหลดตัวเลือกให้ตรงกับค่านั้น
  const restore = useCallback(
    prev => {
      setSelected(prev)
      loadDepartment(prev.division).then(() =>
        loadSection(prev.department).then(() => loadPlant(prev.section)),
      )
    },
    [loadDepartment, loadSection, loadPlant],
  )

  return {
    options,
    loading,
    selected,
    restore,
    handlers: {
      onDivisionChange,
      onDepartmentChange,
      onSectionChange,
      onPlantChange,
    },
  }
}
